import React, { MouseEventHandler, ReactNode } from 'react';
import {
    Container,
    TextContainer,
    IconAndTextContainer,
    MediaContainer,
    Title
} from '../HBCalloutPopover.styles';
import {
    IBasicAction,
    THBCalloutPopoverTheme
} from '../HBCalloutPopover.types';
import { getCalloutPopoverStyles } from '../HBCalloutPopover.styles';
import { Lightbulb24 } from '@honeybook/icons';
import {
    CalloutStaticContentProps,
    TCalloutTheme
} from '../../common/Callout/Callout.types';
import HBCalloutPopoverActionsAndPagination from './HBCalloutPopoverActionsAndPagination';
import { MustIncludeOneOfBasicTestIdProps } from '../../common/internal/test-id.types';
import { useBuildTestIdInternal } from '../../../hooks/useBuildTestIdInternal';
import HBCloseButton from '../../HBCloseButton/HBCloseButton';
import HBText from '../../HBText/HBText';
import { useWindowSize } from '../../../hooks/useWindowSize';
import styled from 'styled-components';
import { upToSmall } from '../../../styles/breakpoints';
import CalloutMediaElement from '../../common/Callout/CalloutMediaElement';

export type ICalloutPopoverContentProps = {
    /** Color theme controlling background, text, button, and close button styling. */
    calloutTheme?: TCalloutTheme;
    /** Renders the primary action as a standard button or as a link-styled button. */
    primaryActionType?: 'button' | 'link-button';
    /** One or two action buttons rendered below the content; first is primary, second is secondary. */
    actions?: [IBasicAction] | [IBasicAction, IBasicAction];
    /** When provided, renders a dismiss (X) button; also disables click-outside-to-close on the parent popover. */
    onClose?: MouseEventHandler<HTMLButtonElement>;
    children?: ReactNode;
    actionsOrientation?: 'horizontal' | 'vertical';
    /** Displays a step indicator (e.g. "1 of 3") next to the action buttons; consumer manages step state externally. */
    stepsContent?: { currStep: number; totalSteps: number };
    /** Click handler for the media element, making it interactive. */
    onImageClick?: () => void;
    /** Shows a lightbulb icon next to the title and description text. */
    withIcon?: boolean;
} & CalloutStaticContentProps;

const HBCalloutPopoverContent = ({
    calloutTheme = 'neutral',
    primaryActionType = 'button',
    onClose,
    children,
    testId,
    testIdName,
    actionsOrientation,
    title,
    description,
    mediaPath,
    actions,
    stepsContent,
    withIcon = true,
    onImageClick
}: ICalloutPopoverContentProps & MustIncludeOneOfBasicTestIdProps) => {
    const primaryActionTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'primary-action',
        componentName: 'HBButton'
    });

    const secondaryActionTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'secondary-action',
        componentName: 'HBButton'
    });

    const { isUpToSmall } = useWindowSize();

    const styles: THBCalloutPopoverTheme =
        getCalloutPopoverStyles(isUpToSmall)[calloutTheme];

    return (
        <>
            <Container>
                {mediaPath && (
                    <MediaContainer>
                        <CalloutMediaElement
                            withImagePadding={false}
                            mediaPath={mediaPath}
                            imageFit={'cover'}
                            layout={'center'}
                            width={'100%'}
                            onClick={onImageClick}
                        />
                    </MediaContainer>
                )}

                <IconAndTextContainer>
                    {withIcon && <Lightbulb24 color={styles.iconColor} />}
                    <TextContainer>
                        {title && (
                            <Title
                                type={isUpToSmall ? 'display24' : 'emphasis16'}
                                color={styles.textColor}
                                align="left"
                                testId="callout-popover-title"
                            >
                                {title}
                            </Title>
                        )}
                        <BodyText
                            type={isUpToSmall ? 'body18' : 'body14'}
                            color={styles.textColor}
                            align="left"
                            testId="callout-popover-description"
                        >
                            {description}
                        </BodyText>
                    </TextContainer>
                </IconAndTextContainer>

                {children && <div>{children}</div>}
                <HBCalloutPopoverActionsAndPagination
                    styles={styles}
                    primaryActionType={primaryActionType}
                    actions={actions}
                    primaryActionTestId={primaryActionTestId}
                    secondaryActionTestId={secondaryActionTestId}
                    actionsOrientation={actionsOrientation}
                    stepsContent={stepsContent}
                />
            </Container>
            {onClose && (
                <HBCloseButton
                    size="xs"
                    onClick={onClose}
                    theme={styles.closeButtonTheme}
                    distance={8}
                    testId={testId}
                    testIdName={testIdName}
                />
            )}
        </>
    );
};

export default HBCalloutPopoverContent;

const BodyText = styled(HBText)`
    ${upToSmall} {
        margin-left: -30px;
    }
`;
